import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    BarChart3, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ChevronUp, Columns, Download, Filter, LayoutList, Maximize2, Minimize2, Search, X
} from 'lucide-react';
import { BarChart, DonutChart, LineChart } from '@tremor/react';
import Modal from './Modal';

export interface DataTableColumn {
    field: string;
    headerName: string;
    width?: number | string;
    renderCell?: (params: { value: any; row: any }) => ReactNode;
    sortable?: boolean;
    /** Épingle la colonne à droite lors du scroll horizontal (ex. colonne Actions). */
    sticky?: boolean;
}

/** Colonne épinglée : flag explicite, ou colonne d'actions (field ou header 'Actions'). */
const isSticky = (col: DataTableColumn) => col.sticky === true || col.field === 'Actions' || col.headerName === 'Actions';

type ViewMode = 'table' | 'chart' | 'list';
type ChartType = 'bar' | 'donut' | 'line';

const PAGE_SIZES = [10, 25, 50, 100];
const MAX_CHART_POINTS = 20;

/**
 * Explorateur de données — remplace MUI X DataGrid.
 *
 * Fonctionnalités : recherche globale, filtres par colonne, tri, pagination
 * (taille de page réglable), visibilité des colonnes, export CSV, vue
 * graphique flexible (barres / donut / courbe avec agrégation), plein écran,
 * et modal de détail au double-clic sur une ligne.
 */
const DataTable = (props: {
    rows: any[];
    columns: DataTableColumn[];
    searchable?: boolean;
    pageSize?: number;
    height?: number | string;
    onRowClick?: (row: any) => void;
    onRowDoubleClick?: (row: any) => void;
    emptyText?: string;
    toolbar?: ReactNode;
    /** Ouvrir le modal de détail au double-clic sur une ligne (défaut true). */
    detailOnClick?: boolean;
    /** Afficher le bouton plein écran (défaut true). */
    fullscreenable?: boolean;
    /** Afficher la vue graphique (défaut true). */
    chartable?: boolean;
}) => {
    const {
        columns, searchable = true, pageSize: initialPageSize = 10, height = 400,
        emptyText = 'Aucune donnée', detailOnClick = true,
        fullscreenable = true, chartable = true,
    } = props;
    const rows = Array.isArray(props.rows) ? props.rows : [];

    const [query, setQuery] = useState('');
    const [colFilters, setColFilters] = useState<Record<string, string>>({});
    const [showFilters, setShowFilters] = useState(false);
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
    const [showColMenu, setShowColMenu] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('table');
    const [fullscreen, setFullscreen] = useState(false);
    // detailRow : ligne dont les actions restent montées (survit à la fermeture du modal)
    // detailOpen : visibilité du modal de détail
    const [detailRow, setDetailRow] = useState<any>(null);
    const [detailOpen, setDetailOpen] = useState(false);

    // Chart state
    const [chartType, setChartType] = useState<ChartType>('bar');
    const [chartCategory, setChartCategory] = useState<string>('');
    const [chartMetric, setChartMetric] = useState<string>('_count');

    const colMenuRef = useRef<HTMLDivElement>(null);

    // Ferme le menu colonnes au clic extérieur
    useEffect(() => {
        if (!showColMenu) return;
        const onDocClick = (e: MouseEvent) => {
            if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) setShowColMenu(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [showColMenu]);

    const visibleColumns = useMemo(
        () => columns.filter(c => !hiddenCols.has(c.field)),
        [columns, hiddenCols]
    );

    /** Colonnes numériques (utilisables comme métrique de graphique). */
    const numericCols = useMemo(
        () => columns.filter(c => rows.some(r => typeof r[c.field] === 'number' && isFinite(r[c.field]))),
        [columns, rows]
    );

    const filtered = useMemo(() => {
        let out = rows;
        const q = query.trim().toLowerCase();
        if (q) {
            out = out.filter((row) =>
                columns.some((col) => {
                    const v = row[col.field];
                    return v != null && String(v).toLowerCase().includes(q);
                })
            );
        }
        const activeFilters = Object.entries(colFilters).filter(([, v]) => v.trim() !== '');
        if (activeFilters.length) {
            out = out.filter((row) =>
                activeFilters.every(([field, f]) => {
                    const v = row[field];
                    return v != null && String(v).toLowerCase().includes(f.trim().toLowerCase());
                })
            );
        }
        return out;
    }, [rows, query, colFilters, columns]);

    const sorted = useMemo(() => {
        if (!sortField) return filtered;
        return [...filtered].sort((a, b) => {
            const va = a[sortField];
            const vb = b[sortField];
            if (va == null) return 1;
            if (vb == null) return -1;
            const cmp = typeof va === 'number' && typeof vb === 'number'
                ? va - vb
                : String(va).localeCompare(String(vb), 'fr', { numeric: true });
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [filtered, sortField, sortDir]);

    const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
    const currentPage = Math.min(page, pageCount - 1);
    const paged = sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
    const rangeStart = sorted.length === 0 ? 0 : currentPage * pageSize + 1;
    const rangeEnd = Math.min(sorted.length, (currentPage + 1) * pageSize);

    const toggleSort = (field: string) => {
        if (sortField === field) {
            if (sortDir === 'asc') setSortDir('desc');
            else { setSortField(null); setSortDir('asc'); }
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const toggleColVisibility = (field: string) => {
        setHiddenCols(prev => {
            const next = new Set(prev);
            if (next.has(field)) next.delete(field);
            else if (next.size < columns.length - 1) next.add(field);
            return next;
        });
    };

    const handleRowClick = (row: any, e: React.MouseEvent) => {
        // Ignorer les clics sur les contrôles internes (boutons d'action, liens, inputs)
        if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [data-no-rowclick]')) return;
        props.onRowClick?.(row);
    };

    const handleRowDoubleClick = (row: any, e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [data-no-rowclick]')) return;
        // Le double-clic ouvre le modal de détail — l'édition passe par
        // les boutons d'action de la colonne dédiée.
        props.onRowDoubleClick?.(row);
        if (detailOnClick) { setDetailRow(row); setDetailOpen(true); }
    };

    /** Export CSV des données filtrées/triées (colonnes visibles). */
    const exportCsv = () => {
        const esc = (v: any) => {
            const s = v == null ? '' : String(v);
            return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const header = visibleColumns.map(c => esc(c.headerName)).join(';');
        const body = sorted.map(row => visibleColumns.map(c => esc(row[c.field])).join(';')).join('\n');
        const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'export.csv';
        a.click();
        URL.revokeObjectURL(a.href);
    };

    /** Agrégation pour la vue graphique : regroupe par catégorie, compte ou somme. */
    const chartData = useMemo(() => {
        if (!chartCategory) return [];
        const groups = new Map<string, number>();
        sorted.forEach(row => {
            const key = row[chartCategory] == null || row[chartCategory] === '' ? '—' : String(row[chartCategory]);
            const val = chartMetric === '_count' ? 1 : (Number(row[chartMetric]) || 0);
            groups.set(key, (groups.get(key) || 0) + val);
        });
        const acc: { name: string; value: number }[] = [];
        groups.forEach((value, name) => acc.push({ name, value: Math.round(value * 100) / 100 }));
        return acc
            .sort((a, b) => b.value - a.value)
            .slice(0, MAX_CHART_POINTS);
    }, [sorted, chartCategory, chartMetric]);

    const toolBtn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-[var(--eb-border)] text-[var(--eb-text-muted)] hover:text-[var(--eb-text)] hover:bg-[var(--eb-bg)] transition-colors';
    const toolBtnActive = 'border-[var(--eb-primary)]/50 text-[var(--eb-primary)] bg-[var(--eb-primary-soft)]/40';

    const chartPanel = viewMode === 'chart' && (
        <div className="rounded-xl border border-[var(--eb-border)] bg-[var(--eb-surface)] p-4">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[var(--eb-text-muted)]">Type</span>
                    <select
                        value={chartType}
                        onChange={(e) => setChartType(e.target.value as ChartType)}
                        className="text-xs px-2 py-1.5 rounded-lg border border-[var(--eb-border)] bg-[var(--eb-surface)] text-[var(--eb-text)] focus:outline-none focus:ring-2 focus:ring-[var(--eb-primary)]/40"
                    >
                        <option value="bar">Barres</option>
                        <option value="donut">Donut</option>
                        <option value="line">Courbe</option>
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[var(--eb-text-muted)]">Regrouper par</span>
                    <select
                        value={chartCategory}
                        onChange={(e) => setChartCategory(e.target.value)}
                        className="text-xs px-2 py-1.5 rounded-lg border border-[var(--eb-border)] bg-[var(--eb-surface)] text-[var(--eb-text)] focus:outline-none focus:ring-2 focus:ring-[var(--eb-primary)]/40"
                    >
                        <option value="">— choisir —</option>
                        {columns.map(c => <option key={c.field} value={c.field}>{c.headerName}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[var(--eb-text-muted)]">Métrique</span>
                    <select
                        value={chartMetric}
                        onChange={(e) => setChartMetric(e.target.value)}
                        className="text-xs px-2 py-1.5 rounded-lg border border-[var(--eb-border)] bg-[var(--eb-surface)] text-[var(--eb-text)] focus:outline-none focus:ring-2 focus:ring-[var(--eb-primary)]/40"
                    >
                        <option value="_count">Nombre de lignes</option>
                        {numericCols.map(c => <option key={c.field} value={c.field}>{c.headerName} (somme)</option>)}
                    </select>
                </div>
                {chartData.length > MAX_CHART_POINTS && (
                    <span className="text-[11px] text-[var(--eb-text-muted)]">Top {MAX_CHART_POINTS} affiché</span>
                )}
            </div>
            {!chartCategory ? (
                <div className="py-16 text-center text-sm text-[var(--eb-text-muted)]">
                    Choisissez une colonne de regroupement pour visualiser les données.
                </div>
            ) : chartData.length === 0 ? (
                <div className="py-16 text-center text-sm text-[var(--eb-text-muted)]">{emptyText}</div>
            ) : chartType === 'donut' ? (
                <DonutChart
                    data={chartData}
                    index="name"
                    category="value"
                    className="h-72"
                    showLabel
                    valueFormatter={(v) => v.toLocaleString('fr-FR')}
                />
            ) : chartType === 'line' ? (
                <LineChart
                    data={chartData}
                    index="name"
                    categories={['value']}
                    className="h-72"
                    yAxisWidth={56}
                    valueFormatter={(v) => v.toLocaleString('fr-FR')}
                />
            ) : (
                <BarChart
                    data={chartData}
                    index="name"
                    categories={['value']}
                    className="h-72"
                    yAxisWidth={56}
                    valueFormatter={(v) => v.toLocaleString('fr-FR')}
                />
            )}
        </div>
    );

    const tablePanel = (viewMode === 'table' || viewMode === 'list') && (
        <>
            {viewMode === 'table' ? (
            <div className={`overflow-auto rounded-xl border border-[var(--eb-border)] bg-[var(--eb-surface)] ${fullscreen ? 'flex-1' : ''}`} style={{ maxHeight: fullscreen ? undefined : height }}>
                <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 z-10 bg-[var(--eb-bg)]">
                        <tr>
                            {visibleColumns.map(col => (
                                <th
                                    key={col.field}
                                    style={{ width: col.width }}
                                    className={`px-3 py-2.5 text-left text-xs font-bold text-[var(--eb-text-muted)] uppercase tracking-wide border-b border-[var(--eb-border)] whitespace-nowrap ${col.sortable !== false ? 'cursor-pointer select-none hover:text-[var(--eb-text)]' : ''} ${isSticky(col) ? 'sticky right-0 z-[11] bg-[var(--eb-bg)] shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.15)]' : ''}`}
                                    onClick={() => col.sortable !== false && toggleSort(col.field)}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        {col.headerName}
                                        {sortField === col.field && (
                                            sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                                        )}
                                    </span>
                                </th>
                            ))}
                        </tr>
                        {showFilters && (
                            <tr className="bg-[var(--eb-surface)]">
                                {visibleColumns.map(col => (
                                    <th key={col.field} className={`px-2 py-1.5 border-b border-[var(--eb-border)] font-normal ${isSticky(col) ? 'sticky right-0 z-[11] bg-[var(--eb-surface)]' : ''}`}>
                                        <input
                                            type="text"
                                            value={colFilters[col.field] || ''}
                                            onChange={(e) => {
                                                setColFilters(prev => ({ ...prev, [col.field]: e.target.value }));
                                                setPage(0);
                                            }}
                                            placeholder="Filtrer…"
                                            className="w-full px-2 py-1 text-xs rounded-md border border-[var(--eb-border)] bg-[var(--eb-bg)] text-[var(--eb-text)] placeholder:text-[var(--eb-text-muted)]/50 focus:outline-none focus:ring-1 focus:ring-[var(--eb-primary)]/40"
                                        />
                                    </th>
                                ))}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {paged.length === 0 && (
                            <tr>
                                <td colSpan={visibleColumns.length} className="px-3 py-10 text-center text-[var(--eb-text-muted)] text-sm">
                                    {emptyText}
                                </td>
                            </tr>
                        )}
                        {paged.map((row, i) => (
                            <tr
                                key={row.id ?? i}
                                onClick={(e) => handleRowClick(row, e)}
                                onDoubleClick={(e) => handleRowDoubleClick(row, e)}
                                className={`border-b border-[var(--eb-border)] last:border-0 transition-colors
                                    ${i % 2 === 1 ? 'bg-[var(--eb-bg)]/40' : ''}
                                    ${props.onRowClick || props.onRowDoubleClick || detailOnClick ? 'cursor-pointer' : ''}
                                    hover:bg-[var(--eb-primary-soft)]/40`}
                            >
                                {visibleColumns.map(col => (
                                    <td key={col.field} className={`px-3 py-2.5 text-[var(--eb-text)] align-middle ${isSticky(col) ? 'sticky right-0 z-[5] bg-[var(--eb-surface)] shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.15)]' : ''}`}>
                                        {col.renderCell ? col.renderCell({ value: row[col.field], row }) : row[col.field]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            ) : (
            /* Vue liste : une carte compacte par ligne */
            <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 ${fullscreen ? 'flex-1 overflow-auto content-start' : ''}`}>
                {paged.length === 0 && (
                    <div className="col-span-full px-3 py-10 text-center text-[var(--eb-text-muted)] text-sm rounded-xl border border-[var(--eb-border)] bg-[var(--eb-surface)]">
                        {emptyText}
                    </div>
                )}
                {paged.map((row, i) => {
                    const dataCols = visibleColumns.filter(c => !isSticky(c));
                    const actionsCol = visibleColumns.find(c => isSticky(c));
                    const titleCol = dataCols[0];
                    const restCols = dataCols.slice(1);
                    return (
                        <div
                            key={row.id ?? i}
                            onClick={(e) => handleRowClick(row, e)}
                            onDoubleClick={(e) => handleRowDoubleClick(row, e)}
                            className="rounded-xl border border-[var(--eb-border)] bg-[var(--eb-surface)] p-3 cursor-pointer transition-all hover:border-[var(--eb-primary)]/50 hover:shadow-md"
                        >
                            {titleCol && (
                                <div className="text-sm font-bold text-[var(--eb-text)] mb-2 truncate">
                                    {titleCol.renderCell ? titleCol.renderCell({ value: row[titleCol.field], row }) : (row[titleCol.field] ?? '—')}
                                </div>
                            )}
                            <dl className="space-y-1">
                                {restCols.map(col => (
                                    <div key={col.field} className="flex items-baseline gap-2 min-w-0">
                                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-[var(--eb-text-muted)] w-24 flex-shrink-0 truncate">
                                            {col.headerName}
                                        </dt>
                                        <dd className="text-xs text-[var(--eb-text)] min-w-0 truncate">
                                            {col.renderCell
                                                ? col.renderCell({ value: row[col.field], row })
                                                : (row[col.field] == null || row[col.field] === '' ? '—' : String(row[col.field]))}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                            {actionsCol?.renderCell && (
                                <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-[var(--eb-border)]">
                                    {actionsCol.renderCell({ value: row[actionsCol.field], row })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            )}

            {/* Pagination */}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-xs text-[var(--eb-text-muted)]">
                <div className="flex items-center gap-3">
                    <span>
                        {sorted.length === 0
                            ? '0 élément'
                            : `${rangeStart}–${rangeEnd} sur ${sorted.length} élément${sorted.length > 1 ? 's' : ''}`}
                        {(query || Object.values(colFilters).some(v => v.trim())) && rows.length !== sorted.length && (
                            <span className="ml-1 opacity-70">(filtré sur {rows.length})</span>
                        )}
                    </span>
                    <select
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                        className="px-2 py-1 rounded-md border border-[var(--eb-border)] bg-[var(--eb-surface)] text-[var(--eb-text)] focus:outline-none"
                    >
                        {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => setPage(0)} disabled={currentPage === 0} className="p-1.5 rounded-md hover:bg-[var(--eb-bg)] disabled:opacity-30 disabled:pointer-events-none"><ChevronsLeft size={15} /></button>
                    <button onClick={() => setPage(currentPage - 1)} disabled={currentPage === 0} className="p-1.5 rounded-md hover:bg-[var(--eb-bg)] disabled:opacity-30 disabled:pointer-events-none"><ChevronLeft size={15} /></button>
                    <span className="px-2 font-medium text-[var(--eb-text)]">{currentPage + 1}/{pageCount}</span>
                    <button onClick={() => setPage(currentPage + 1)} disabled={currentPage >= pageCount - 1} className="p-1.5 rounded-md hover:bg-[var(--eb-bg)] disabled:opacity-30 disabled:pointer-events-none"><ChevronRight size={15} /></button>
                    <button onClick={() => setPage(pageCount - 1)} disabled={currentPage >= pageCount - 1} className="p-1.5 rounded-md hover:bg-[var(--eb-bg)] disabled:opacity-30 disabled:pointer-events-none"><ChevronsRight size={15} /></button>
                </div>
            </div>
        </>
    );

    const content = (
        <div className={fullscreen
            ? 'fixed inset-0 z-[3000] bg-[var(--eb-bg)] p-4 sm:p-6 flex flex-col overflow-hidden'
            : 'w-full'}>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    {searchable && (
                        <div className="relative w-64 max-w-full">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--eb-text-muted)]" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => { setQuery(e.target.value); setPage(0); }}
                                placeholder="Rechercher…"
                                className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-[var(--eb-border)] bg-[var(--eb-surface)] text-[var(--eb-text)] placeholder:text-[var(--eb-text-muted)]/60 focus:outline-none focus:ring-2 focus:ring-[var(--eb-primary)]/40 focus:border-[var(--eb-primary)]"
                            />
                        </div>
                    )}
                    <button
                        onClick={() => setShowFilters(v => !v)}
                        className={`${toolBtn} ${showFilters ? toolBtnActive : ''}`}
                        title="Filtres par colonne"
                    >
                        <Filter size={14} /> Filtres
                        {Object.values(colFilters).some(v => v.trim()) && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--eb-primary)]" />
                        )}
                    </button>
                    <div className="relative" ref={colMenuRef}>
                        <button onClick={() => setShowColMenu(v => !v)} className={toolBtn} title="Colonnes visibles">
                            <Columns size={14} /> Colonnes
                        </button>
                        {showColMenu && (
                            <div className="absolute left-0 top-full mt-1 z-30 min-w-[200px] py-1 rounded-lg border border-[var(--eb-border)] bg-[var(--eb-surface)] shadow-xl">
                                {columns.map(col => (
                                    <label key={col.field} className="flex items-center gap-2 px-3 py-1.5 text-xs text-[var(--eb-text)] hover:bg-[var(--eb-bg)] cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={!hiddenCols.has(col.field)}
                                            onChange={() => toggleColVisibility(col.field)}
                                            className="accent-[var(--eb-primary)]"
                                        />
                                        {col.headerName}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    <button onClick={exportCsv} className={toolBtn} title="Exporter en CSV">
                        <Download size={14} /> CSV
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    {props.toolbar}
                    {chartable && (
                        <div className="flex rounded-lg border border-[var(--eb-border)] overflow-hidden">
                            <button
                                onClick={() => setViewMode('table')}
                                className={`px-2.5 py-1.5 text-xs font-medium ${viewMode === 'table' ? 'bg-[var(--eb-primary)] text-white' : 'text-[var(--eb-text-muted)] hover:bg-[var(--eb-bg)]'}`}
                            >Tableau</button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-2.5 py-1.5 text-xs font-medium inline-flex items-center gap-1 ${viewMode === 'list' ? 'bg-[var(--eb-primary)] text-white' : 'text-[var(--eb-text-muted)] hover:bg-[var(--eb-bg)]'}`}
                            ><LayoutList size={13} /> Liste</button>
                            <button
                                onClick={() => setViewMode('chart')}
                                className={`px-2.5 py-1.5 text-xs font-medium inline-flex items-center gap-1 ${viewMode === 'chart' ? 'bg-[var(--eb-primary)] text-white' : 'text-[var(--eb-text-muted)] hover:bg-[var(--eb-bg)]'}`}
                            ><BarChart3 size={13} /> Graphique</button>
                        </div>
                    )}
                    {fullscreenable && (
                        <button
                            onClick={() => setFullscreen(v => !v)}
                            className={toolBtn}
                            title={fullscreen ? 'Quitter le plein écran' : 'Plein écran'}
                        >
                            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                        </button>
                    )}
                    {fullscreen && (
                        <button onClick={() => setFullscreen(false)} className={toolBtn} title="Fermer">
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>

            {chartPanel}
            {tablePanel}
        </div>
    );

    return (
        <>
            {fullscreen ? createPortal(content, document.body) : content}

            {/* Modal de détail de la ligne cliquée (double-clic).
                keepMounted : les boutons d'action gardent leur état (ex. FormDrawer
                portailé) après fermeture du dialogue — sinon l'action ne s'exécuterait pas. */}
            <Modal
                open={detailOpen}
                keepMounted
                onClose={() => setDetailOpen(false)}
                title="Détail de la ligne"
                width={560}
            >
                {detailRow && (() => {
                    const actionsCol = columns.find(c => isSticky(c));
                    return (
                        <>
                            {/* Actions mises en évidence — un clic ferme le dialogue,
                                l'action elle-même s'exécute (composant conservé monté) */}
                            {actionsCol?.renderCell && (
                                <div
                                    className="flex items-center justify-between gap-3 mb-4 px-3 py-2.5 rounded-lg border border-[var(--eb-primary)]/30 bg-[var(--eb-primary-soft)]/30"
                                    onClick={() => setDetailOpen(false)}
                                >
                                    <span className="text-[11px] font-bold uppercase tracking-wide text-[var(--eb-primary)]">Actions</span>
                                    <div className="flex items-center gap-1">
                                        {actionsCol.renderCell({ value: detailRow[actionsCol.field], row: detailRow })}
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                {(() => {
                                    // Toutes les clés de la ligne : colonnes déclarées d'abord (hors Actions), puis le reste
                                    const declared = columns.map(c => c.field).filter(f => f !== actionsCol?.field);
                                    const extras = Object.keys(detailRow).filter(k => !declared.includes(k) && k !== actionsCol?.field && k !== 'id' && !k.startsWith('_') && k !== 'raw');
                                    const ordered = [...declared, ...extras];
                                    return ordered.map(field => {
                                        const col = columns.find(c => c.field === field);
                                        const value = detailRow[field];
                                        const display = col?.renderCell
                                            ? col.renderCell({ value, row: detailRow })
                                            : (value == null || value === '' ? '—' : (typeof value === 'object' ? JSON.stringify(value) : String(value)));
                                        return (
                                            <div key={field} className="min-w-0">
                                                <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--eb-text-muted)]">
                                                    {col?.headerName || field}
                                                </div>
                                                <div className="text-sm text-[var(--eb-text)] break-words">
                                                    {display}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </>
                    );
                })()}
            </Modal>
        </>
    );
};

export default DataTable;
